import axios, { CancelTokenSource } from 'axios';
import AddButton from 'components/AddButton';
import DeleteActionButton from 'components/DeleteActionButton';
import EditActionButton from 'components/EditActionButton';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import PxGrid from 'components/layout/PxGrid';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import UpsertCovidTestAnalyzerForm from 'components/medicaments/covid-test-analyzer/UpsertCovidTestAnalyzerForm';
import SearchResultComponent from 'components/SearchResultComponent';
import React, { Component } from 'react';
import { Form as FinalForm } from 'react-final-form';
import { MessageSourceProps, withMessages } from "react-message-source";
import { Dimmer, Grid, Loader, Message, Modal } from 'semantic-ui-react';
import {
  deleteCovidTestAnalyzer,
  findCovidTestAnalyzerById,
  getAllCovidTestAnalyzers
} from 'services/covidTestAnalyzerServices';
import { getAllTestLaboratory } from 'services/testLaboratoryServices';
import { CovidTestAnalyzerDto, TestLaboratoryDto, UpsertCovidTestAnalyzerDto } from 'ts-types/api.types';
import { noop } from 'utils/functionUtils';

interface Props extends MessageSourceProps {
}

interface State {
  foundAnalyzers: Array<CovidTestAnalyzerDto>,
  analyzersLoaded: boolean,
  covidTestAnalyzer?: UpsertCovidTestAnalyzerDto,
  covidTestAnalyzerId?: number,
  laboratories: TestLaboratoryDto[],
  addEditModalOpen: boolean,
  cancelTokenSource: CancelTokenSource,
  errorMessage?: string
}

class CovidTestAnalyzerView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      foundAnalyzers: [],
      analyzersLoaded: false,
      covidTestAnalyzer: undefined,
      covidTestAnalyzerId: undefined,
      laboratories: [],
      addEditModalOpen: false,
      cancelTokenSource
    };

    this.fetchPageData();
  }

  fetchPageData = async () => {

    const {cancelTokenSource} = this.state;

    try {
      const testLaboratories = await getAllTestLaboratory(cancelTokenSource);
      const covidTestAnalyzers = await getAllCovidTestAnalyzers(cancelTokenSource);

      this.setState({
        laboratories: testLaboratories,
        foundAnalyzers: covidTestAnalyzers
      });
    } finally {
      this.setState({
        analyzersLoaded: true
      });
    }
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel("Operation canceled by user");
  }

  openAddEditModal = (id: number | undefined) => {
    const {cancelTokenSource} = this.state;

    if (id) {
      findCovidTestAnalyzerById(id, cancelTokenSource)
          .then(response => {
            this.showModal(id, response);
          })
          .catch(noop);
    }
  };

  showModal = (id?: number, analyzer?: UpsertCovidTestAnalyzerDto) => {
    this.setState(({}: State) => {
      return {
        addEditModalOpen: true,
        covidTestAnalyzer: analyzer,
        covidTestAnalyzerId: id
      };
    });
  };

  hideModal = () => {
    this.setState(({}: State) => ({
      addEditModalOpen: false,
      covidTestAnalyzer: undefined,
      covidTestAnalyzerId: undefined
    }));
  };

  onSaveUpsertForm = () => {
    this.hideModal();
    this.fetchPageData();
  }

  deleteAnalyzer = (id: number): void => {
    const {cancelTokenSource} = this.state;
    deleteCovidTestAnalyzer(id, cancelTokenSource)
        .then(response => {
          this.setState(({foundAnalyzers}: State) => ({
            foundAnalyzers: foundAnalyzers.filter(
                (analyzer: CovidTestAnalyzerDto) => analyzer.id != response
            )
          }));
        })
        .catch((error) => this.handleError(error.response.data));
  };

  handleError = (error: any) => {
    const {getMessage} = this.props;
    this.setErrorMessage(getMessage("error.general"));
  }

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  }

  render(): React.ReactNode {
    const {analyzersLoaded} = this.state;
    return (
        <PraxStyledSegment>
          <div>
            {this.renderTopSegment()}
          </div>
          <StyledViewTableRow>
            {!analyzersLoaded
                ? this.renderLoader()
                : this.renderAnalyzersSegment()}
          </StyledViewTableRow>

          {this.renderUpsertCovidTestAnalyzerModal()}
        </PraxStyledSegment>
    );
  }

  renderTopSegment = (): JSX.Element => {

    return (
        <FinalForm
            onSubmit={() => {
            }}
            initialValues={{}}
            subscription={{pristine: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = (): React.ReactNode => {
    const {getMessage} = this.props;
    const {errorMessage, foundAnalyzers} = this.state;

    const numResults = foundAnalyzers.length;

    return (
        <PxGrid>
          {errorMessage && this.renderErrorSegment()}
          <Grid.Row>
            <Grid.Column width={16} style={{textAlign: 'right'}}>
              <AddButton buttonMessage={getMessage("button.add")} onClick={() => this.showModal()}/>
            </Grid.Column>
          </Grid.Row>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          {/*TODO KJ 2021-12-23: FormSpy will be used in case we implement search*/}
          {/*<FormSpy subscription={{values: true}}/>*/}
        </PxGrid>
    );
  };

  renderErrorSegment = (): React.ReactNode => {
    const {errorMessage} = this.state;

    return (
        <Grid.Row>
          <Grid.Column>
            <Message negative onDismiss={() => this.setErrorMessage()}>
              <p>{errorMessage}</p>
            </Message>
          </Grid.Column>
        </Grid.Row>
    );
  };

  renderLoader = (): React.ReactNode => {
    const {getMessage} = this.props;

    return (
        <Dimmer active inverted>
          <Loader inline="centered" size="medium">
            {getMessage("covidTestAnalyzer.loading")}
          </Loader>
        </Dimmer>
    );
  };

  renderAnalyzersSegment = (): JSX.Element => {
    const {getMessage} = this.props;
    const {foundAnalyzers} = this.state;

    return (
        <VirtualizedTable
            rowCount={foundAnalyzers.length}
            rowGetter={this.analyzerRowGetter}
            rowRenderer={this.analyzerRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "index"
              },
              {
                width: 150,
                flexGrow: 1,
                label: getMessage("covidTestAnalyzer.code"),
                dataKey: "code"
              },
              {
                width: 250,
                flexGrow: 1,
                label: getMessage("covidTestAnalyzer.description"),
                dataKey: "description"
              },
              {
                width: 250,
                label: getMessage("covidTestAnalyzer.laboratory"),
                dataKey: "laboratoryId",
                cellRenderer: this.laboratoryCellRenderer
              },
              {
                width: 250,
                label: getMessage("covidTestAnalyzer.testMethod"),
                dataKey: "testMethod",
                cellRenderer: this.testMethodCellRenderer
              },
              {
                width: 100,
                label: getMessage("actions"),
                dataKey: "id",
                cellRenderer: this.actionsCellRenderer
              }
            ]}
        />
    );
  };

  analyzerRowGetter = ({index}: any) => {
    const {foundAnalyzers} = this.state;

    Object.assign(foundAnalyzers[index], {index: index + 1});

    return foundAnalyzers[index];
  };

  analyzerRowRenderer = ({className, columns, index, key, style}: any) => {

    const a11yProps = {"aria-rowindex": index + 1};
    const {foundAnalyzers} = this.state;
    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openAddEditModal(foundAnalyzers[index].id)}
        >
          {columns}
        </div>
    );
  };

  laboratoryCellRenderer = ({cellData}: any) => {
    const {laboratories} = this.state;

    if (cellData && cellData > 0) {
      const foundTL = laboratories.filter((tl: TestLaboratoryDto) => tl.id === cellData)
      if (foundTL && foundTL.length > 0) {
        return foundTL[0].name;
      }
    }
    return "";
  }

  testMethodCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;
    if (cellData) {
      return getMessage(`enum.AnalyzerTestMethod.${cellData}`)
    } else {
      return ""
    }
  }

  actionsCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage("msgEdit")}
              onConfirm={() => this.openAddEditModal(cellData)}
          />
          <DeleteActionButton
              hoverMessage={getMessage("msgDelete")}
              popupMessage={getMessage("covidTestAnalyzer.confirmDelete")}
              onConfirm={() => this.deleteAnalyzer(cellData)}
          />
        </React.Fragment>
    );
  };

  renderUpsertCovidTestAnalyzerModal = (): React.ReactNode => {
    const {getMessage} = this.props;
    const {addEditModalOpen, covidTestAnalyzer, covidTestAnalyzerId, laboratories} = this.state;

    return (
        <Modal open={addEditModalOpen} closeOnDimmerClick={false} onClose={() => this.hideModal()}>
          <Modal.Header>{getMessage("covidTestAnalyzer.title")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertCovidTestAnalyzerForm
                covidTestAnalyzer={covidTestAnalyzer}
                covidTestAnalyzerId={covidTestAnalyzerId}
                laboratories={laboratories}
                onSave={this.onSaveUpsertForm}
                onCancel={this.hideModal}
            />
          </FormModalContentContainer>
        </Modal>
    );
  };
}

export default withMessages(CovidTestAnalyzerView)
